import Icon from "@/components/icon";
import { humanizeSearchLabel, type RecentSearch } from "@/hooks/use-recent-searches";
import { STARTER_REPORTS } from "@/routes/starter-reports";
import { CollapsibleSection } from "@/views/collapsible-section";

// The empty-homepage view (signed-in, no active search): the hero above the
// search box and the recents/starters shortcuts below it. The search box
// itself stays in the route component so it never remounts when these
// sections come and go.

// Returning users get a compact task heading; first-run users get the same
// task-oriented copy at a more welcoming scale.
export function HomeHero({ compact }: { compact: boolean }) {
  if (compact) {
    return (
      <header className="pt-2 text-center sm:pt-6">
        <h1 className="mx-auto max-w-2xl text-balance font-serif text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl mt-4 sm:mt-0">
          Wyszukaj lek
        </h1>
        {/* text-base like the full hero: one size for this role everywhere. */}
        <p className="mx-auto mt-2 max-w-xl text-base leading-relaxed text-muted-foreground">
          Sprawdź, co wiadomo o jego wpływie na płodność, ciążę i&nbsp;karmienie piersią
        </p>
      </header>
    );
  }
  return (
    <header className="pt-4 text-center sm:pt-10">
      <h1 className="mx-auto max-w-2xl text-balance font-serif text-[1.75rem] font-semibold leading-[1.1] tracking-tight text-foreground sm:text-5xl sm:leading-[1.05]">
        Wyszukaj lek
      </h1>
      <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
        Sprawdź, co wiadomo o jego wpływie na płodność, ciążę i&nbsp;karmienie piersią
      </p>
    </header>
  );
}

interface HomeShortcutsProps {
  // Per-user history: tapping one reattaches the cached report instantly.
  recents: RecentSearch[];
  onOpen: (query: string, label: string) => void;
}

// A fragment (not a wrapper div) so both sections keep participating in the
// page's flex gap individually, exactly as when they were inlined. Both are
// permanent on the empty homepage (decision 2026-08-15) — recents when they
// exist, the example chips always.
export function HomeShortcuts({ recents, onOpen }: HomeShortcutsProps) {
  return (
    <>
      {recents.length > 0 && (
        <CollapsibleSection
          storageKey="recent-searches"
          title="Ostatnie wyszukiwania"
          className="mx-auto w-full max-w-2xl"
        >
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {/* Labels go through humanizeSearchLabel at render too: rows
                stored before the write-side normaliser existed may still
                carry the raw "+"-joined URL slug as their label. */}
            {recents.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-label={`Otwórz ponownie raport: ${humanizeSearchLabel(item.label)}`}
                onClick={() => onOpen(item.query, item.label)}
                className="group flex min-h-14 items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left shadow-sm transition-[border-color,background-color,box-shadow] hover:border-brand/35 hover:bg-brand/[0.04] hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                  <Icon name="history" className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                  {humanizeSearchLabel(item.label)}
                </span>
                <Icon
                  name="chevron-right"
                  className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                  aria-hidden="true"
                />
              </button>
            ))}
          </div>
        </CollapsibleSection>
      )}

      <CollapsibleSection
        storageKey="starter-reports"
        title="Przykładowe raporty"
        className="mx-auto w-full max-w-2xl"
      >
        {/* Chips, not tiles: ~10 one-tap reports across specialties read as
            examples drawn from a large library, and the compact form keeps
            the whole set visible without dominating the page. */}
        <div className="mt-4 flex flex-wrap gap-2">
          {STARTER_REPORTS.map((report) => (
            <button
              key={report.query}
              type="button"
              aria-label={`Otwórz przykładowy raport: ${report.label}`}
              onClick={() => onOpen(report.query, report.label)}
              className="rounded-full border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground shadow-sm transition-[border-color,background-color,box-shadow] hover:border-brand/35 hover:bg-brand/[0.04] hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {report.label}
            </button>
          ))}
        </div>
      </CollapsibleSection>
    </>
  );
}
